import type { ElementType } from "react";

import { cn } from "@/utils/cn";

import type { ITextProps } from "./types";
import { textVariants } from "./variants";

export default function Text(props: ITextProps) {
  const { as: Tag = "span", size, weight, variant, className, children, ...rest } = props;

  return (
    <Tag className={cn(textVariants({ size, weight, variant }), className)} {...rest}>
      {children}
    </Tag>
  );
}

/** Preset axis values baked into a `Text.*` shortcut. */
type TextPreset = Pick<ITextProps, "size" | "weight" | "variant">;

/**
 * Builds the fixed-variant shortcuts exposed as `Text.Accent`, `Text.Hint`, …
 * Any axis the preset leaves unset stays overridable at the call site
 * (e.g. `<Text.Hint size="caption">`).
 */
function createVariant(preset: TextPreset, defaultTag: ElementType = "span") {
  return function TextVariant({
    as: Tag = defaultTag,
    size,
    weight,
    variant,
    className,
    children,
    ...rest
  }: ITextProps) {
    return (
      <Tag
        className={cn(
          textVariants({
            size: size ?? preset.size,
            weight: weight ?? preset.weight,
            variant: variant ?? preset.variant,
          }),
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  };
}

Text.Caption = createVariant({ size: "caption" });
Text.Sub = createVariant({ variant: "sub" });
Text.Hint = createVariant({ variant: "hint" });
Text.Link = createVariant({ variant: "link" });
Text.Title = createVariant({ size: "title", weight: "semibold" }, "h1");
Text.Accent = createVariant({ variant: "accent" });
Text.Danger = createVariant({ variant: "danger" });
Text.Warning = createVariant({ variant: "warning" });
